/**
 * Layout / overlay adapters for the procedural AMHS kit (default 3D stocker / equipment).
 */
import React from 'react';
import { Stocker3D } from './amhs/Stocker/Stocker3D';
import { AMHSEquipment } from './amhs/Equipment/AMHSEquipment';
import type { AMHSEquipmentType } from './amhs/types';
import { AMHS_EQUIPMENT_TINTS, resolveKitFoupScale } from './amhs/types';
import type { AmhsEquipmentTint } from './amhs/types';
import { defaultElementDepth, resolveEquipmentKitHeight } from '../../utils/layout3d';
import type { LayoutElement } from '../../types';
import type { FrameVehicle } from '../../hooks/useSimulationFrames';
import type { LayoutStockerPortRef } from './amhs/Stocker/StockerLoadPortBank';
import { useEquipmentPortTransfer } from './amhs/Equipment/useEquipmentFoupTransfer';
import { resolveEquipment3DPortFacing } from './amhs/Equipment/equipmentFacing3d';
import {
  dockFractionsAlongFace,
  portsOf,
  resolveElementLoadPortCount,
} from './amhs/Equipment/equipmentPortOwnership';
import { resolveProcessEquipmentKitWidth } from './amhs/Equipment/equipmentPortTransfer';
import { useAppSelector } from '../../hooks/redux';

/** Realistic tool height so footprints don't become flat blue slabs. */
export { resolveEquipmentKitHeight };

/**
 * Pick procedural equipment type from layout metadata / footprint.
 * Layout tools use process equipment + load ports (or conveyor / standalone LP).
 */
export function resolveAmhsEquipmentType(
  element?: Pick<LayoutElement, 'type' | 'metadata' | 'customProperties' | 'dimensions'> | null,
  fallback: AMHSEquipmentType = 'AMHS_INTERFACE'
): AMHSEquipmentType {
  if (!element) return fallback;
  const t = element.type;
  // STB = standalone load port; everything else is a process tool with load ports.
  if (t === 'stb') return 'LOAD_PORT';
  if (t === 'ohb' || t === 'lifter') return 'AMHS_INTERFACE';

  const name = `${element.metadata?.name ?? ''} ${element.customProperties?.processType ?? ''} ${
    element.customProperties?.equipmentType ?? ''
  }`.toLowerCase();

  if (/convey|belt|roller/.test(name)) return 'CONVEYOR';
  if (/load\s*port|stb|lp[-_\s]/.test(name)) return 'LOAD_PORT';
  if (/buffer|ohb|shelf|transfer|lifter|crane|robot|interface|amhs|tool|equip/.test(name)) {
    return 'AMHS_INTERFACE';
  }

  return fallback;
}

/** Buffers, lifters and process tools share one shell, so colour separates them. */
function resolveAmhsEquipmentTint(
  element?: Pick<LayoutElement, 'type'> | null,
  resolved?: AMHSEquipmentType
): AmhsEquipmentTint {
  if (element?.type === 'ohb') return AMHS_EQUIPMENT_TINTS.buffer;
  if (element?.type === 'lifter') return AMHS_EQUIPMENT_TINTS.tool;
  if (element?.type === 'stb' || resolved === 'LOAD_PORT') return AMHS_EQUIPMENT_TINTS.loadPort;
  return AMHS_EQUIPMENT_TINTS.tool;
}

export const AmhsStockerFromLayout3D: React.FC<{
  w: number;
  h: number;
  opacity?: number;
  bayCount?: string | number;
  /** Outside / central stocker: 4 tray-face ports only (no back side). */
  outsideStocker?: boolean;
  storedFoupsCount?: number;
  label?: string;
  /** Layout element id for sim port matching / FOUP transfer. */
  elementId?: string;
  detailLod?: 'near' | 'far';
}> = ({
  w,
  h,
  opacity = 1,
  bayCount,
  outsideStocker = false,
  storedFoupsCount = 0,
  label,
  elementId,
  detailLod: _detailLod = 'near',
}) => {
  void _detailLod;
  const depth = defaultElementDepth('stocker');
  const layoutElement = useAppSelector((s) =>
    elementId ? s.layout.elements[elementId] : undefined
  );
  const layoutPorts = Array.isArray(layoutElement?.customProperties?.ports)
    ? (layoutElement.customProperties.ports as LayoutStockerPortRef[])
    : undefined;
  const kitW = Math.max(w, 1);
  const kitD = Math.max(h, 1);
  const kitH = Math.max(depth, 1);

  return (
    <group position={[w / 2, 0, h / 2]}>
      <Stocker3D
        id={elementId ?? 'stocker'}
        label={label ?? layoutElement?.metadata?.name}
        width={kitW}
        depth={kitD}
        height={kitH}
        bayCount={bayCount}
        outsideStocker={outsideStocker}
        layoutPorts={layoutPorts}
        occupiedCount={storedFoupsCount}
        status={storedFoupsCount > 0 ? 'running' : 'ready'}
        detail="high"
        autoDetail={false}
        opacity={opacity}
      />
    </group>
  );
};

export const AmhsLoadPortFromLayout3D: React.FC<{
  w: number;
  h: number;
  depth: number;
  opacity?: number;
  foupPresent?: boolean;
  label?: string;
  element?: LayoutElement | null;
  simulationFrames?: FrameVehicle[];
}> = ({
  w,
  h,
  depth,
  opacity = 1,
  foupPresent,
  label = 'LP',
  element,
}) => {
  const toolH = resolveEquipmentKitHeight(w, h, depth);
  const facing = resolveEquipment3DPortFacing(element);
  const eqW = Math.max(facing.swapsFootprintAxes ? h : w, 1);
  const eqD = Math.max(facing.swapsFootprintAxes ? w : h, 1);
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  const portFractions = dockFractionsAlongFace(
    element ? portsOf(element, equipmentInstances) : [],
    1
  );
  const fromSim = useEquipmentPortTransfer({
    element,
    width: eqW,
    height: toolH,
    depth: eqD,
    loadPortCount: 1,
    standaloneLoadPort: true,
  });
  const showFoup = foupPresent ?? fromSim.foupPresent;
  const flying = fromSim.flyingFoup
    ? {
        position: fromSim.flyingFoup.position,
        scaleHint: resolveKitFoupScale(eqW, toolH, eqD),
      }
    : null;

  return (
    <group position={[w / 2, 0, h / 2]} rotation={[0, facing.rotationY, 0]}>
      <AMHSEquipment
        type="LOAD_PORT"
        width={eqW}
        height={toolH}
        depth={eqD}
        foupPresent={showFoup}
        portFractions={portFractions}
        portVisuals={fromSim.portVisuals}
        flyingFoup={flying}
        isTransferring={fromSim.isTransferring}
        transferProgress01={fromSim.transferProgress01 ?? 0}
        transferOperation={fromSim.transferOperation ?? 'PICKUP'}
        status={fromSim.equipmentStatus}
        label={label}
        opacity={opacity}
        tint={resolveAmhsEquipmentTint(element, 'LOAD_PORT')}
      />
    </group>
  );
};

/**
 * Replaces blue placeholder / AmhsTool3D boxes with procedural AMHS equipment.
 * FOUPs + shutters animate while an OHT is transferring at this equipment.
 */
export const AmhsEquipmentFromLayout3D: React.FC<{
  w: number;
  h: number;
  depth: number;
  opacity?: number;
  element?: LayoutElement | null;
  type?: AMHSEquipmentType;
  label?: string;
  foupPresent?: boolean;
  loadPortCount?: number;
  occupiedLoadPorts?: number[];
  simulationFrames?: FrameVehicle[];
}> = ({
  w,
  h,
  depth,
  opacity = 1,
  element,
  type,
  label,
  foupPresent,
  loadPortCount,
  occupiedLoadPorts,
}) => {
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);
  // 2D draws one dock per stored port, so 3D must do the same or the sim will
  // animate a shutter the event never referenced.
  const portCount =
    loadPortCount ?? resolveElementLoadPortCount(element, equipmentInstances);
  const resolved = type ?? resolveAmhsEquipmentType(element);
  const toolH = resolveEquipmentKitHeight(w, h, depth);
  const caption = label ?? element?.metadata?.name ?? resolved;

  const longX = w >= h;
  const facing = resolveEquipment3DPortFacing(element);
  const eqW =
    resolved === 'CONVEYOR'
      ? Math.max(w, h)
      : Math.max(facing.swapsFootprintAxes ? h : w, 1);
  const eqD =
    resolved === 'CONVEYOR'
      ? Math.min(w, h)
      : Math.max(facing.swapsFootprintAxes ? w : h, 1);
  const rotY =
    resolved === 'CONVEYOR'
      ? !longX
        ? Math.PI / 2
        : 0
      : facing.rotationY;
  const eqH = resolved === 'CONVEYOR' ? Math.max(depth, toolH * 0.55) : toolH;
  // Use the 2D footprint width so spaced tools stay spaced in 3D (do not force
  // EQUIPMENT_KIT_WIDTH — that made neighboring cabinets look merged).
  const kitW =
    resolved === 'CONVEYOR' || resolved === 'LOAD_PORT'
      ? eqW
      : resolveProcessEquipmentKitWidth(portCount, eqW);

  const portFractions = dockFractionsAlongFace(
    element ? portsOf(element, equipmentInstances) : [],
    resolved === 'LOAD_PORT' ? 1 : portCount
  );
  const fromSim = useEquipmentPortTransfer({
    element,
    width: kitW,
    height: eqH,
    depth: eqD,
    loadPortCount: resolved === 'LOAD_PORT' ? 1 : portCount,
    standaloneLoadPort: resolved === 'LOAD_PORT',
    fabProcessTool: resolved === 'AMHS_INTERFACE',
  });
  const showFoup = foupPresent ?? fromSim.foupPresent;
  const ports =
    occupiedLoadPorts ??
    (resolved === 'AMHS_INTERFACE' || resolved === 'LOAD_PORT'
      ? fromSim.occupiedLoadPorts
      : undefined);
  const flying = fromSim.flyingFoup
    ? {
        position: fromSim.flyingFoup.position,
        scaleHint: resolveKitFoupScale(kitW, eqH, eqD),
      }
    : null;

  return (
    <group position={[w / 2, 0, h / 2]} rotation={[0, rotY, 0]}>
      <AMHSEquipment
        type={resolved}
        width={kitW}
        height={eqH}
        depth={eqD}
        foupPresent={showFoup}
        loadPortCount={portCount}
        occupiedLoadPorts={ports}
        portFractions={portFractions}
        portVisuals={
          resolved === 'LOAD_PORT' ? fromSim.portVisuals : undefined
        }
        flyingFoup={resolved === 'CONVEYOR' || resolved === 'AMHS_INTERFACE' ? null : flying}
        isTransferring={
          resolved === 'CONVEYOR' ? false : fromSim.isTransferring
        }
        transferProgress01={fromSim.transferProgress01 ?? 0}
        transferOperation={fromSim.transferOperation ?? 'PICKUP'}
        layoutElement={resolved === 'AMHS_INTERFACE' ? element : null}
        status={
          resolved === 'CONVEYOR'
            ? 'RUNNING'
            : fromSim.equipmentStatus
        }
        label={caption}
        opacity={opacity}
        tint={resolveAmhsEquipmentTint(element, resolved)}
      />
    </group>
  );
};

export default AmhsStockerFromLayout3D;
